import { FC } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'next-i18next'
import {
  CompanyOrderBase,
  CompanyOrderFieldBase,
  CompanySize,
  CurrencyUnit,
  ILocationBase,
  IMarketBase,
  OrderDirection,
} from '@/types'
import { getValidCompanySize } from '@/utils'
import { KeywordSearchGroupFormForCompanies } from '@components-v2/molecules/KeywordSearchGroupFormForCompanies'
import { InvestmentTargetAmountSelectField } from '@components-v2/molecules/InvestmentTargetAmountSelectField'

import { LocationSearchSelectField } from '@components-v2/molecules/LocationSearchSelectField'
import { MarketSearchSelectField } from '@components-v2/molecules/MarketSearchSelectField'
import { RoundsMultipleSelectField } from '@components-v2/molecules/RoundsMultipleSelectField'
import { CompanySizeSelectField2 } from '@components-v2/molecules/CompanySizeSelectField2'
import styles from './index.module.scss'

type Value = {
  keyword: string
  markets: IMarketBase[]
  companySize: CompanySize
  locations: ILocationBase[]
  roundIds: string[]
  orderBy: CompanyOrderBase
  investmentAmount: {
    maxAmount?: string
    minAmount?: string
  }
  unit?: CurrencyUnit
}

type Props = {
  isVc?: boolean
  search: (
    keyword: string,
    markets: string[],
    companySize: CompanySize,
    locations: string[],
    roundIds: string[],
    orderBy: CompanyOrderBase,
    maxAmount?: string,
    minAmount?: string,
    unit?: CurrencyUnit
  ) => Promise<void>
}

export const KeywordSearchGroupForCompanies: FC<Props> = ({ isVc = false, search }) => {
  const { t } = useTranslation(['angels-vcs-startups', 'search'])
  const initialValue: Value = {
    keyword: '',
    markets: [],
    companySize: undefined,
    locations: [],
    roundIds: [],
    orderBy: {
      direction: OrderDirection.DESC,
      companyOrderField: isVc ? CompanyOrderFieldBase.VC_SCORE : CompanyOrderFieldBase.STARTUP_SCORE,
    },
    investmentAmount: undefined,
    unit: CurrencyUnit.USD,
  }
  const validationSchema = Yup.object().shape({})

  const setSearch = async (values: Value) => {
    await search(
      values.keyword,
      values.markets.map((market) => market.id),
      isVc ? values.companySize : getValidCompanySize(values.companySize),
      values.locations.map((location) => location.id),
      values.roundIds,
      values.orderBy,
      values.investmentAmount?.maxAmount,
      values.investmentAmount?.minAmount,
      values.unit
    )
  }

  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validationSchema}
      onSubmit={(values: Value, actions) => {
        setSearch(values)
        actions.setSubmitting(false)
      }}
    >
      {({ setFieldValue, values, submitForm, isSubmitting }) => {
        // 不恰好だが一旦これで submit 走らせる
        const setCompanyOrderField = (v: CompanyOrderFieldBase) => {
          setFieldValue('orderBy.companyOrderField', v)
          submitForm()
        }
        const setLocations = (v: ILocationBase[]) => {
          setFieldValue('locations', v)
          submitForm()
        }
        const setMarkets = (v: IMarketBase[]) => {
          setFieldValue('markets', v)
          submitForm()
        }
        const setRoundIds = (v: string[]) => {
          setFieldValue('roundIds', v)
          submitForm()
        }
        const setCompanySize = (v: CompanySize) => {
          setFieldValue('companySize', v)
          submitForm()
        }
        const setInvestmentAmount = (v: { maxAmount?: string; minAmount?: string }) => {
          setFieldValue('investmentAmount', v)
          submitForm()
        }

        return (
          <KeywordSearchGroupFormForCompanies
            isVc={isVc}
            selectedCompanyOrder={values.orderBy}
            setCompanyOrderField={setCompanyOrderField}
            isSubmitting={isSubmitting}
          >
            <div className={styles.container}>
              <LocationSearchSelectField
                placeholder={t('所在地')}
                selectedLocations={values.locations}
                setFieldValue={setLocations}
              />
              {isVc && (
                <>
                  <InvestmentTargetAmountSelectField
                    placeholder={t('平均投資金額')}
                    selectedValue={values.investmentAmount}
                    setFieldValue={setInvestmentAmount}
                  />
                  <RoundsMultipleSelectField
                    placeholder={t('投資対象ラウンド')}
                    selectedRoundIds={values.roundIds}
                    setFieldValue={setRoundIds}
                  />
                  <MarketSearchSelectField
                    placeholder={t('投資対象領域')}
                    selectedMarkets={values.markets}
                    setFieldValue={setMarkets}
                  />
                </>
              )}
              {!isVc && (
                <>
                  <RoundsMultipleSelectField
                    placeholder={t('ラウンド')}
                    selectedRoundIds={values.roundIds}
                    setFieldValue={setRoundIds}
                  />
                  <MarketSearchSelectField
                    placeholder={t('業界')}
                    selectedMarkets={values.markets}
                    setFieldValue={setMarkets}
                  />
                </>
              )}
              <CompanySizeSelectField2
                placeholder={t('会社規模')}
                selectedCompanySize={values.companySize}
                setFieldValue={setCompanySize}
              />
            </div>
          </KeywordSearchGroupFormForCompanies>
        )
      }}
    </Formik>
  )
}
